import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Hammer, Users, Zap, Clock, TrendingUp, Coins, Server, Copy, CheckCircle, RefreshCw, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

interface PoolStats {
  hashrate: string;
  miners: number;
  workers: number;
  blocksFound: number;
  lastBlock: number;
  networkHashrate: string;
  networkDifficulty: string;
  poolFee: string;
  minPayout: string;
  payoutInterval: string;
}

interface Miner {
  address: string;
  hashrate: string;
  shares: number;
  lastSeen: number;
  pending: number;
  paid: number;
}

interface Block {
  height: number;
  hash: string;
  time: number;
  reward: number;
  finder?: string;
  confirmed: boolean;
}

type Chain = 'WTX' | 'HTH';

const MiningPoolView: React.FC = () => {
  const [activeChain, setActiveChain] = useState<Chain>('WTX');
  const [poolStats, setPoolStats] = useState<PoolStats | null>(null);
  const [topMiners, setTopMiners] = useState<Miner[]>([]);
  const [recentBlocks, setRecentBlocks] = useState<Block[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [minerAddress, setMinerAddress] = useState('');
  const [minerStats, setMinerStats] = useState<Miner | null>(null);

  // Pool configuration
  const poolConfig = {
    WTX: {
      name: 'WATTx Mining Pool',
      stratumUrl: 'stratum+tcp://pool.wattxchange.app:4444',
      stratumSSL: 'stratum+ssl://pool.wattxchange.app:5555',
      algorithm: 'RandomX (Merged Mining with XMR)',
      coin: 'WTX',
      website: 'https://pool.wattxchange.app',
      apiUrl: 'https://pool.wattxchange.app/api/pool'
    },
    HTH: {
      name: 'HTH Mining Pool',
      stratumUrl: 'stratum+tcp://hth-pool.wattxchange.app:3333',
      stratumSSL: 'stratum+ssl://hth-pool.wattxchange.app:3334',
      algorithm: 'X25X PoW',
      coin: 'HTH',
      website: 'https://hth-pool.wattxchange.app',
      apiUrl: 'https://hth-pool.wattxchange.app/api'
    }
  };

  useEffect(() => {
    loadPoolData();
    const interval = setInterval(loadPoolData, 30000);
    return () => clearInterval(interval);
  }, [activeChain]);

  const loadPoolData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchPoolStats(),
        fetchTopMiners(),
        fetchRecentBlocks()
      ]);
    } catch (error) {
      console.error('Failed to load pool data:', error);
      toast.error('Failed to load pool data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPoolStats = async () => {
    try {
      const response = await fetch(`${poolConfig[activeChain].apiUrl}/stats`);
      if (!response.ok) throw new Error('API request failed');

      const data = await response.json();

      // Format hashrate for display
      const formatHashrate = (hashrate: number | null) => {
        if (!hashrate) return '0 H/s';
        if (hashrate >= 1e12) return `${(hashrate / 1e12).toFixed(2)} TH/s`;
        if (hashrate >= 1e9) return `${(hashrate / 1e9).toFixed(2)} GH/s`;
        if (hashrate >= 1e6) return `${(hashrate / 1e6).toFixed(2)} MH/s`;
        if (hashrate >= 1e3) return `${(hashrate / 1e3).toFixed(2)} KH/s`;
        return `${hashrate.toFixed(2)} H/s`;
      };

      const formatDifficulty = (diff: number) => {
        if (!diff) return 'N/A';
        if (diff >= 1e9) return `${(diff / 1e9).toFixed(2)}B`;
        if (diff >= 1e6) return `${(diff / 1e6).toFixed(2)}M`;
        if (diff >= 1e3) return `${(diff / 1e3).toFixed(2)}K`;
        return diff.toFixed(2);
      };

      setPoolStats({
        hashrate: formatHashrate(data.pool_stats?.hashrate || data.pool?.hashrate || 0),
        miners: data.pool_stats?.miners || data.pool?.miners || 0,
        workers: data.pool_stats?.workers || data.pool?.workers || 0,
        blocksFound: data.pool_stats?.blocksFound || data.pool?.blocksFound || 0,
        lastBlock: data.pool_stats?.lastBlockTime ? data.pool_stats.lastBlockTime * 1000 : Date.now(),
        networkHashrate: formatHashrate(data.network?.hashrate || data.network?.networkHashPs || 0),
        networkDifficulty: formatDifficulty(data.network?.difficulty || 0),
        poolFee: `${data.pool?.fee || 1}%`,
        minPayout: `${data.pool?.minPayout || 1} ${activeChain}`,
        payoutInterval: 'Every 2 hours'
      });
    } catch (error) {
      console.error('Failed to fetch pool stats:', error);
      // Fallback to default values on error
      setPoolStats({
        hashrate: '0 H/s',
        miners: 0,
        workers: 0,
        blocksFound: 0,
        lastBlock: Date.now(),
        networkHashrate: 'N/A',
        networkDifficulty: 'N/A',
        poolFee: '1%',
        minPayout: `1 ${activeChain}`,
        payoutInterval: 'Every 2 hours'
      });
    }
  };

  const fetchTopMiners = async () => {
    // Mock top miners
    const miners: Miner[] = Array.from({ length: 10 }, (_, i) => ({
      address: activeChain === 'WTX'
        ? `wx1q${Math.random().toString(36).slice(2, 38)}`
        : `H${Math.random().toString(36).slice(2, 34)}`,
      hashrate: `${(Math.random() * 50 + 5).toFixed(2)} MH/s`,
      shares: Math.floor(Math.random() * 10000) + 1000,
      lastSeen: Date.now() - Math.random() * 300000,
      pending: Math.random() * 10,
      paid: Math.random() * 100
    }));
    setTopMiners(miners);
  };

  const fetchRecentBlocks = async () => {
    // Mock recent blocks found by pool
    const blocks: Block[] = Array.from({ length: 10 }, (_, i) => ({
      height: activeChain === 'WTX' ? 17245 - i * 50 : 892451 - i * 20,
      hash: `0x${Math.random().toString(16).slice(2, 66)}`,
      time: Date.now() - i * 7200000,
      reward: activeChain === 'WTX' ? 50 : 10,
      confirmed: i > 2
    }));
    setRecentBlocks(blocks);
  };

  const lookupMiner = async () => {
    if (!minerAddress.trim()) {
      toast.error('Please enter a miner address');
      return;
    }

    toast.loading('Looking up miner...', { id: 'lookup' });

    // Mock miner lookup
    setTimeout(() => {
      const stats: Miner = {
        address: minerAddress,
        hashrate: `${(Math.random() * 20 + 1).toFixed(2)} MH/s`,
        shares: Math.floor(Math.random() * 5000) + 500,
        lastSeen: Date.now() - Math.random() * 60000,
        pending: Math.random() * 5,
        paid: Math.random() * 50
      };
      setMinerStats(stats);
      toast.success('Miner found!', { id: 'lookup' });
    }, 1000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedText(null), 2000);
  };

  const formatTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const truncateAddress = (address: string, length: number = 8) => {
    return `${address.slice(0, length)}...${address.slice(-length)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-emerald-400 bg-clip-text text-transparent">
            Mining Pools
          </h1>
          <p className="text-gray-400 mt-1">{poolConfig[activeChain].name}</p>
        </div>

        {/* Chain Selector */}
        <div className="flex space-x-2">
          {(['WTX', 'HTH'] as Chain[]).map((chain) => (
            <button
              key={chain}
              onClick={() => setActiveChain(chain)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeChain === chain
                  ? chain === 'WTX'
                    ? 'bg-gradient-to-r from-yellow-600 to-emerald-600 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {chain}
            </button>
          ))}
          <button
            onClick={loadPoolData}
            disabled={isLoading}
            className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Pool Stats */}
      {poolStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Zap className="w-4 h-4" />
              <span className="text-sm">Pool Hashrate</span>
            </div>
            <p className="text-xl font-bold text-yellow-400">{poolStats.hashrate}</p>
          </div>
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Active Miners</span>
            </div>
            <p className="text-xl font-bold text-white">{poolStats.miners}</p>
          </div>
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Server className="w-4 h-4" />
              <span className="text-sm">Workers</span>
            </div>
            <p className="text-xl font-bold text-white">{poolStats.workers}</p>
          </div>
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Hammer className="w-4 h-4" />
              <span className="text-sm">Blocks Found</span>
            </div>
            <p className="text-xl font-bold text-emerald-400">{poolStats.blocksFound}</p>
          </div>
          <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
            <div className="flex items-center space-x-2 text-gray-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Last Block</span>
            </div>
            <p className="text-xl font-bold text-white">{formatTime(poolStats.lastBlock)}</p>
          </div>
        </motion.div>
      )}

      {/* Connection Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
      >
        <h2 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Server className="w-5 h-5 text-yellow-400" />
          <span>How to Connect</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">Stratum URL (TCP)</p>
            <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
              <code className="text-yellow-400 text-sm">{poolConfig[activeChain].stratumUrl}</code>
              <button
                onClick={() => copyToClipboard(poolConfig[activeChain].stratumUrl)}
                className="text-gray-400 hover:text-white ml-2"
              >
                {copiedText === poolConfig[activeChain].stratumUrl ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">Stratum URL (SSL)</p>
            <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
              <code className="text-yellow-400 text-sm">{poolConfig[activeChain].stratumSSL}</code>
              <button
                onClick={() => copyToClipboard(poolConfig[activeChain].stratumSSL)}
                className="text-gray-400 hover:text-white ml-2"
              >
                {copiedText === poolConfig[activeChain].stratumSSL ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Algorithm:</span>
            <span className="ml-2 text-white">{poolConfig[activeChain].algorithm}</span>
          </div>
          <div>
            <span className="text-gray-400">Pool Fee:</span>
            <span className="ml-2 text-emerald-400">{poolStats?.poolFee}</span>
          </div>
          <div>
            <span className="text-gray-400">Min Payout:</span>
            <span className="ml-2 text-white">{poolStats?.minPayout}</span>
          </div>
        </div>
      </motion.div>

      {/* Miner Lookup */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
      >
        <h2 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <Activity className="w-5 h-5 text-emerald-400" />
          <span>Miner Lookup</span>
        </h2>
        <div className="flex space-x-4 mb-4">
          <input
            type="text"
            value={minerAddress}
            onChange={(e) => setMinerAddress(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && lookupMiner()}
            placeholder={`Enter your ${activeChain} address...`}
            className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500/50"
          />
          <button
            onClick={lookupMiner}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              activeChain === 'WTX'
                ? 'bg-gradient-to-r from-yellow-600 to-emerald-600 hover:from-yellow-500 hover:to-emerald-500'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500'
            } text-white`}
          >
            Lookup
          </button>
        </div>

        {minerStats && (
          <div className="bg-gray-800/50 rounded-lg p-4 grid md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-400">Hashrate</p>
              <p className="text-lg font-bold text-yellow-400">{minerStats.hashrate}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Shares</p>
              <p className="text-lg font-bold text-white">{minerStats.shares.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Pending</p>
              <p className="text-lg font-bold text-emerald-400">{minerStats.pending.toFixed(4)} {activeChain}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Paid</p>
              <p className="text-lg font-bold text-white">{minerStats.paid.toFixed(4)} {activeChain}</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Top Miners & Recent Blocks */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Miners */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden"
        >
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
              <span>Top Miners</span>
            </h2>
          </div>
          <div className="divide-y divide-gray-800 max-h-80 overflow-y-auto">
            {topMiners.map((miner, index) => (
              <div key={miner.address} className="p-4 hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index < 3 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-white">{truncateAddress(miner.address)}</p>
                      <p className="text-sm text-gray-400">{formatTime(miner.lastSeen)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-yellow-400">{miner.hashrate}</p>
                    <p className="text-sm text-gray-400">{miner.shares.toLocaleString()} shares</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Blocks */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden"
        >
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold flex items-center space-x-2">
              <Coins className="w-5 h-5 text-emerald-400" />
              <span>Blocks Found</span>
            </h2>
          </div>
          <div className="divide-y divide-gray-800 max-h-80 overflow-y-auto">
            {recentBlocks.map((block) => (
              <div key={block.height} className="p-4 hover:bg-gray-800/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-emerald-400">#{block.height.toLocaleString()}</p>
                    <p className="text-sm text-gray-400">{formatTime(block.time)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-white">{block.reward} {activeChain}</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      block.confirmed
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {block.confirmed ? 'Confirmed' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MiningPoolView;
